import { api } from "@/features/utils/fetch-server";
import type {
  CallbackRequest,
  CallbackStatus,
  CompanyProposalState,
  Onboarding,
  OnboardingDraft,
  OnboardingResult,
  OnboardingState,
} from "../types";

const base = (clientId: string) => `/api/onboarding/v1/${clientId}`;

export type SaveDraftPayload = {
  draft?: OnboardingDraft;
  current_step?: string;
  website?: string;
  /**
   * The token `/auth/phone/verify` returns. The only way to get a verified
   * mobile onto the draft — the server re-checks it and ignores anything the
   * draft itself claims about verification.
   */
  phone_challenge_token?: string;
};

export class OnboardingAPI {
  static getState = async (clientId: string): Promise<OnboardingState> =>
    api.get<OnboardingState>(base(clientId));

  /**
   * Start the analysis. Returns as soon as the job is queued — progress is read
   * from `analysis_status` by polling `getState`.
   */
  static startAnalysis = async (
    clientId: string,
    input: { url?: string; description?: string },
  ): Promise<Onboarding> => {
    const response = await api.post<
      { onboarding: Onboarding },
      { url?: string; description?: string }
    >(`${base(clientId)}/analyze`, input);
    return response.onboarding;
  };

  static saveDraft = async (
    clientId: string,
    payload: SaveDraftPayload,
  ): Promise<Onboarding> => {
    const response = await api.put<
      { onboarding: Onboarding },
      SaveDraftPayload
    >(`${base(clientId)}/draft`, payload);
    return response.onboarding;
  };

  static complete = async (
    clientId: string,
    draft?: OnboardingDraft,
  ): Promise<OnboardingResult> =>
    api.post<OnboardingResult, { draft?: OnboardingDraft }>(
      `${base(clientId)}/complete`,
      { draft },
    );

  /** "Appelez-moi": the agent calls the owner's verified mobile from the demo line. */
  static requestCallback = async (clientId: string): Promise<CallbackRequest> =>
    api.post<CallbackRequest, undefined>(`${base(clientId)}/callback`);

  static getCallbackStatus = async (
    clientId: string,
    sessionId: string,
  ): Promise<CallbackStatus> => {
    const response = await api.get<{ status: CallbackStatus }>(
      `${base(clientId)}/callback/${sessionId}`,
    );
    return response.status;
  };

  /**
   * Put the wizard back to `pending`, so it runs again — for the client who
   * wants another pass, or for support resetting a tenant.
   */
  static restart = async (clientId: string): Promise<Onboarding> => {
    const response = await api.post<{ onboarding: Onboarding }, undefined>(
      `${base(clientId)}/restart`,
    );
    return response.onboarding;
  };

  static skip = async (clientId: string): Promise<Onboarding> => {
    const response = await api.post<{ onboarding: Onboarding }, undefined>(
      `${base(clientId)}/skip`,
    );
    return response.onboarding;
  };

  /**
   * "Remplir depuis mon site" in the company settings: the wizard's analysis,
   * returned as a proposal — the company itself is never written.
   */
  static getCompanyProposal = async (
    clientId: string,
  ): Promise<CompanyProposalState> => {
    const response = await api.get<{ state: CompanyProposalState }>(
      `${base(clientId)}/company-proposal`,
    );
    return response.state;
  };

  /** Returns as soon as the run is recorded — poll `getCompanyProposal`. */
  static startCompanyProposal = async (
    clientId: string,
    input: { url?: string },
  ): Promise<CompanyProposalState> => {
    const response = await api.post<
      { state: CompanyProposalState },
      { url?: string }
    >(`${base(clientId)}/company-proposal`, input);
    return response.state;
  };

  static dismissCompanyProposal = async (
    clientId: string,
  ): Promise<CompanyProposalState> => {
    const response = await api.delete<{ state: CompanyProposalState }>(
      `${base(clientId)}/company-proposal`,
    );
    return response.state;
  };
}
